import { formatDistanceToNow } from 'date-fns';
import PaymentsIcon from '@mui/icons-material/Payments';

import type { Payment } from '@models/payments';
import { CaptureStates } from '@models/payments';
import { UserPermissions } from '@models/user';
import { useCapturePaymentMutation } from '@features/payments/mutations';
import { formatPaymentAmount } from '@features/payments/helpers';
import { useMeStore } from '@features/users/store';
import { useConfirm } from '@hooks/useConfirm';
import { userHasPermission } from '@utils/access';

import s from './CapturePaymentButton.module.css';

// Captured, migration mirrors (captured on the old gateway) and payments outside the capture pipeline
// have nothing to capture — the API rejects those too, this just keeps the button out of the way.
const CAPTURABLE_STATES: string[] = [
  CaptureStates.AWAITING,
  CaptureStates.REQUESTED,
  CaptureStates.EXPIRED,
];

type CapturePaymentButtonProps = {
  payment: Payment;
};

function CapturePaymentButton({ payment }: CapturePaymentButtonProps) {
  const capturePayment = useCapturePaymentMutation();
  const confirm = useConfirm();
  const me = useMeStore();

  // Capturing moves money, so it is a write action
  if (!me || !userHasPermission(me, UserPermissions.EDIT_USERS)) return null;
  if (!payment.captureState || !CAPTURABLE_STATES.includes(payment.captureState)) return null;

  const isExpired = payment.captureState === CaptureStates.EXPIRED;
  // A capture the provider accepted is only stamped on the payment when the settle webhook lands, so
  // the state stays 'requested' in between — say so, otherwise the button reads as if nothing happened.
  const isRequested = payment.captureState === CaptureStates.REQUESTED;
  const windowClosesAt = payment.captureWindowClosesAt
    ? formatDistanceToNow(new Date(payment.captureWindowClosesAt), { addSuffix: true })
    : null;
  const amountLabel = `${formatPaymentAmount(payment.amount, payment.currency, payment.currency.decimalDigits)} ${payment.currency.code}`;

  const windowHint = isExpired
    ? `Capture window closed${windowClosesAt ? ` ${windowClosesAt}` : ''} — the provider will likely reject this`
    : isRequested
      ? `Capture already requested — waiting for the provider to settle it${windowClosesAt ? `, window closes ${windowClosesAt}` : ''}`
      : windowClosesAt
        ? `Capture window closes ${windowClosesAt}`
        : '';

  const handleClick = async () => {
    // The dialog renders the message as a single paragraph, so keep it to plain sentences.
    const confirmed = await confirm(
      [
        `This asks the provider to capture ${amountLabel} for this payment right away, instead of waiting for the next capture run.`,
        payment.captureState === CaptureStates.REQUESTED
          ? 'A capture was already requested for this payment, so the once-a-day guard the cron applies will be bypassed.'
          : '',
        isExpired
          ? 'Its capture window has already closed, so the provider will most likely reject this.'
          : '',
      ]
        .filter(Boolean)
        .join(' '),
      {
        title: 'Capture this payment now?',
        confirmLabel: 'Capture payment',
        cancelLabel: 'Cancel',
        tone: isExpired ? 'danger' : 'success',
      },
    );
    if (!confirmed) return;

    capturePayment.mutate({ paymentId: payment.id, paymentCreatedAt: payment.createdAt });
  };

  return (
    <div className={s.wrap}>
      <button
        type="button"
        className={s.captureBtn}
        onClick={handleClick}
        disabled={capturePayment.isPending}>
        <PaymentsIcon sx={{ fontSize: 15 }} />
        {capturePayment.isPending ? 'Capturing…' : isRequested ? 'Capture again' : 'Capture now'}
      </button>
      {windowHint && <span className={s.hint}>{windowHint}</span>}
    </div>
  );
}

export default CapturePaymentButton;
